"use client"

import * as React from "react"
import { Table } from "@tanstack/react-table"
import { addDays, endOfDay, startOfDay } from "date-fns";
import { getCommissionPartners, getLocations, getReservations, getReservationStates, getVehicles } from "../services/reservation-service"
import { CalendarEvent, CalendarLocation, CalendarPartner, CalendarVehicle, Mode, ReservationState } from "../calendar/calendar-types"
import { CalendarContext } from "../calendar/calendar-context"
import CalendarNewEventDialog from "../calendar/dialog/calendar-new-event-dialog"
import CalendarManageEventDialog from "../calendar/dialog/calendar-manage-event-dialog"
import CalendarHeaderActionsAdd from "../calendar/header/actions/calendar-header-actions-add"
import OrderDetails from "./OrderDetails";
import { DataTableFacetedFilter } from "@/components/data-table/data-table-faceted-filter";
import { Input } from "@/components/ui/input";
import { DatePickerWithRange } from "@/components/custom/date-picker-with-range";
import { DateRange } from "react-day-picker";
import { useState } from "react";
import { getColumns } from "./data-table/Columns";
import { mixedPresets } from "@/constants/date-preset";
import { CheckCircle, CircleOff, Timer } from "lucide-react";
import { DataTable } from "@/components/data-table/data-table";

export const OrdersList = () => {

    const [data, setData] = React.useState<CalendarEvent[]>([]);
    const [mode, setMode] = React.useState<Mode>('month')
    const [date, setDate] = React.useState<Date>(new Date())
    const [locations, setLocations] = React.useState<CalendarLocation[]>([]);
    const [partners, setPartners] = React.useState<CalendarPartner[]>([]);
    const [vehicles, setVehicles] = React.useState<CalendarVehicle[]>([]);
    const [reservationStates, setReservationStates] = React.useState<ReservationState[]>(getReservationStates());
    const [selectedOrder, setSelectedOrder] = React.useState<CalendarEvent | null>(null);
    const [isSheetOpen, setIsSheetOpen] = React.useState(false);

    const [newEventDialogOpen, setNewEventDialogOpen] = React.useState(false)
    const [manageEventDialogOpen, setManageEventDialogOpen] = React.useState(false)
    const [selectedEvent, setSelectedEvent] = React.useState<CalendarEvent | null>(null)

    const [dateFrom, setDateFrom] = React.useState<Date>(startOfDay(new Date()));
    const [dateTo, setDateTo] = React.useState<Date>(endOfDay(new Date()));

    const [dateRange, setDateRange] = useState<DateRange>({ from: new Date(), to: addDays(new Date(), 7) });

    const statuses = [
        {
            value: "pending",
            label: "Pending",
            icon: Timer,
        },
        {
            value: "approved",
            label: "Approved",
            icon: CheckCircle,
        },
        {
            value: "rejected",
            label: "Rejected",
            icon: CircleOff,
        },
    ]

    React.useEffect(() => {
        const unsub = async () => {
            setLocations(await getLocations());
            setVehicles(await getVehicles());
            setPartners(await getCommissionPartners());
        }
        unsub();
    }, [])

    React.useEffect(() => {
        const unsub = async () => {
            getReservations(dateFrom, dateTo)
                .then((response) => {
                    setData(response);
                });
        }
        unsub();

    }, [dateFrom, dateTo]);

    const handleEditClick = (e: React.MouseEvent, event: CalendarEvent) => {
        e.preventDefault();
        e.stopPropagation();

        setSelectedEvent(event);
        setManageEventDialogOpen(true);
    };

    const handleRowClick = (row: CalendarEvent) => {
        setSelectedOrder(row);
        setIsSheetOpen(true);
    };

    // Function to render toolbar filter components - accepts table as parameter
    const renderToolbarFilters = (table: Table<CalendarEvent>) => {
        return (
            <>
                <DatePickerWithRange
                    title="Filter by booking start"
                    date={dateRange}
                    presets={mixedPresets}
                    setDate={(range) => {
                        console.log("Date Range: " + range);
                        if (range) {
                            setDateRange(range);

                            setDateFrom(range.from ? startOfDay(range.from) : startOfDay(new Date()));
                            setDateTo(range.to ? endOfDay(range.to) : endOfDay(new Date()));
                        }
                    }}
                />

                <Input
                    placeholder="Search..."
                    value={(table?.getColumn("title")?.getFilterValue() as string) ?? ""}
                    onChange={(event) =>
                        table?.getColumn("title")?.setFilterValue(event.target.value)
                    }
                    className="h-8 w-[150px] lg:w-[250px]"
                />

                {table?.getColumn("status") && (
                    <DataTableFacetedFilter
                        column={table.getColumn("status")}
                        title="Status"
                        options={statuses}
                    />
                )}
            </>
        );
    };

    // Reset all filters function
    const handleResetFilters = () => {
        // This will be implemented when we have table instance access
        console.log("Reset filters clicked");
    };

    return (
        <div className="mt-2 p-5">
            <CalendarContext.Provider
                value={{
                    events: data,
                    setEvents: setData,
                    mode,
                    setMode,
                    date,
                    setDate,
                    calendarIconIsToday: true,
                    newEventDialogOpen,
                    setNewEventDialogOpen,
                    manageEventDialogOpen,
                    setManageEventDialogOpen,
                    selectedEvent,
                    setSelectedEvent,
                    locations,
                    setLocations,
                    partners,
                    setPartners,
                    vehicles,
                    setVehicles,
                    reservationStates,
                    setReservationStates
                }}>

                <CalendarNewEventDialog />
                <CalendarManageEventDialog />

                <DataTable
                    data={data}
                    columns={getColumns(handleEditClick)}
                    renderToolbarFilters={renderToolbarFilters}
                    onResetFilters={handleResetFilters}
                    onRowClick={handleRowClick}
                    rightActions={<CalendarHeaderActionsAdd />}
                />
                {selectedOrder && (
                    <OrderDetails
                        selectedOrder={selectedOrder}
                        isSheetOpen={isSheetOpen}
                        setIsSheetOpen={setIsSheetOpen}
                    />
                )}

            </CalendarContext.Provider>
        </div>
    )
}

export default OrdersList;